import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Bell, User, LogOut, Moon, Sun } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const Header = () => {
  const { user, logout, authFetch } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useI18n();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ contacts: [], companies: [], deals: [] });
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Notifications
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifs = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/notifications?only_unread=false`;
      const resp = await authFetch(url, { method: 'GET' });
      if (resp.ok) {
        const data = await resp.json();
        setNotifs(data || []);
        setUnreadCount((data || []).filter(n => !n.is_read).length);
      }
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const markAllRead = async () => {
    try {
      const resp = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-all-read`, { method: 'POST' });
      if (resp.ok) fetchNotifs();
    } catch {}
  };

  // Debounce query
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults({ contacts: [], companies: [], deals: [] });
      setOpen(false);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/search?q=${encodeURIComponent(query)}&limit=5`;
        const resp = await authFetch(url, { method: 'GET' });
        if (resp.ok) {
          const data = await resp.json();
          setResults(data);
          setOpen(true);
        }
      } catch (e) {
        // silent fail for search
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, authFetch]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const groupHasResults = (group) => (results[group] && results[group].length > 0);

  const ResultGroup = ({ title, group, toPrefix }) => {
    if (!groupHasResults(group)) return null;
    return (
      <div className="py-2">
        <div className="px-3 text-xs font-semibold text-gray-500">{title}</div>
        {results[group].map((item) => (
          <a
            key={item.id}
            href={`${toPrefix}/${item.id}`}
            className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md"
            onClick={() => setOpen(false)}
          >
            <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
              {item.name || item.title}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {item.email || item.domain || item.stage || ''}
            </div>
          </a>
        ))}
      </div>
    );
  };

  return (
    <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 fixed top-0 left-0 md:left-64 right-0 z-40">
      <div className="h-full px-4 md:px-6 flex items-center justify-between">
        {/* Mobile: Show brand, Desktop: Show search */}
        <div className="flex-1 max-w-xl">
          <div className="md:hidden">
            <h1 className="text-xl font-bold text-bissa-blue ml-12">Bissa</h1>
          </div>
          <div className="hidden md:block relative" ref={dropdownRef}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('header.searchPlaceholder', 'Search contacts, companies, deals...')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0176D3] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            {open && (groupHasResults('contacts') || groupHasResults('companies') || groupHasResults('deals')) && (
              <div className="absolute mt-2 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2 max-h-96 overflow-auto">
                <ResultGroup title={t('nav.contacts')} group="contacts" toPrefix="/contacts" />
                <ResultGroup title={t('nav.companies')} group="companies" toPrefix="/companies" />
                <ResultGroup title={t('nav.deals')} group="deals" toPrefix="/deals" />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          {/* Language Switcher */}
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="hidden sm:block px-2 py-1 border rounded md:text-sm text-xs"
          >
            <option value="auto">AUTO</option>
            <option value="en">EN</option>
            <option value="es">ES</option>
            <option value="fr">FR</option>
            <option value="sw">SW</option>
            <option value="ar">AR</option>
            <option value="pt">PT</option>
            <option value="ha">HA</option>
            <option value="am">AM</option>
            <option value="yo">YO</option>
            <option value="ig">IG</option>
          </select>
          <button 
            onClick={toggleTheme}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            {isDark ? (
              <Sun className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <Moon className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            )}
          </button>
          
          <div className="relative hidden sm:block">
            <button onClick={() => setNotifOpen((v) => !v)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors relative">
              <Bell className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2 max-h-96 overflow-auto z-50">
                <div className="flex items-center justify-between px-2 py-1">
                  <div className="text-sm font-semibold">Notifications</div>
                  <button className="text-xs text-blue-600" onClick={markAllRead}>Mark all as read</button>
                </div>
                {notifs.length === 0 ? (
                  <div className="text-sm text-gray-500 px-2 py-4">No notifications</div>
                ) : (
                  <ul className="space-y-1">
                    {notifs.map((n) => (
                      <li key={n.id} className={`px-2 py-2 rounded ${n.is_read ? '' : 'bg-blue-50 dark:bg-gray-800'}`}>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{n.title}</div>
                        {n.body && <div className="text-xs text-gray-600 dark:text-gray-400">{n.body}</div>}
                        <div className="text-[10px] text-gray-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none">
              <Avatar className="cursor-pointer">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#0176D3] text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5 text-sm">
                <p className="font-medium dark:text-gray-100">{user?.name}</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs">{user?.email}</p>
              </div>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings?tab=profile')}>
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings?tab=preferences')}>
                <span className="w-4 h-4 mr-2">⚙️</span>
                Preferences
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings?tab=integrations')}>
                <span className="w-4 h-4 mr-2">🔗</span>
                Connected accounts
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings?tab=notifications')}>
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;